// React
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

// User Actions
import { updateProfile, getUserDetails } from "../../actions/userActions";

// Internal Components
import Header from "../../components/layouts/Header/Header";
import TestFooter from "../../components/layouts/Footer/TestFooter";

// Stylesheet
import "./profile.css";
import SEO from "../../components/seo/SEO";

export default function ProfileScreen() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux State: User Login (Source of Truth for Auth)
  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo, loading: loginLoading } = userLogin;

  // Redux State: User Update (For success messages)
  const userUpdate = useSelector((state) => state.userUpdate);
  const { loading: updateLoading, success } = userUpdate;

  // Local State
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);

  // 1. Auth Check & Data Synchronization
  useEffect(() => {
    if (!userInfo && !loginLoading) {
      return;
    } else if (userInfo) {
      if (!name || success) {
        setName(userInfo.name);
        setEmail(userInfo.email);
      }
    }
  }, [dispatch, navigate, userInfo, loginLoading, success, name]);

  useEffect(() => {
    if (!userInfo && !loginLoading) {
      return;
    }
  }, [dispatch, userInfo, loginLoading]);

  useEffect(() => {
    if (success) {
      setShowSuccess(true);
      setMessage(null);

      setPassword("");
      setConfirmPassword("");

      dispatch(getUserDetails());

      const timer = setTimeout(() => setShowSuccess(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [success, dispatch]);

  const submitHandler = (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setMessage("Passwords do not match");
    } else {
      setMessage(null);

      // 1. Create the base object with always-present fields
      const updatedUser = {
        id: userInfo._id,
        name,
        email,
      };

      // 2. Only add password to the object if it is not empty
      if (password) {
        updatedUser.password = password;
      }

      try {
        dispatch(updateProfile(updatedUser));
        console.log("SUCCESS");
      } catch (error) {
        console.log("profile update error = ", error);
      }

      // 3. Dispatch the cleaned object
    }
  };

  const isLoading = loginLoading || updateLoading;

  return (
    <>
      <SEO
        title="My Profile"
        description="Explore your journal log, update your details and look at some frequently asked questions."
        canonicalUrl={"https://nowasteddays.io/my-account/menu"}
      />
      <Header variant="light" />
      <main className="mainProfileScreen">
        {/* Success Feedback Toast */}
        {showSuccess && (
          <div className="success-toast">Profile Updated Successfully</div>
        )}

        {isLoading ? (
          <></>
        ) : (
          <section className="profile-card">
            <h1 className="profile-title">User Settings</h1>

            {message && <div className="error-message">{message}</div>}

            <form className="profile-form" onSubmit={submitHandler}>
              <div className="form-group">
                <label htmlFor="name" className="input-label">
                  Name
                </label>
                <input
                  id="name"
                  type="text"
                  className="clean-input"
                  value={name}
                  placeholder="Enter name"
                  onChange={(e) => setName(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label htmlFor="email" className="input-label">
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  className="clean-input"
                  value={email}
                  placeholder="Enter email"
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div className="section-divider"></div>

              <h2 className="sub-header">Security</h2>

              <div className="form-group">
                <label htmlFor="password" className="input-label">
                  New Password
                </label>
                <input
                  id="password"
                  type="password"
                  className="clean-input"
                  value={password}
                  placeholder="Enter new password"
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                />
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword" className="input-label">
                  Confirm Password
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  className="clean-input"
                  value={confirmPassword}
                  placeholder="Confirm new password"
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                />
              </div>

              <div className="action-row">
                <button className="btn-primary" type="submit">
                  Update Profile
                </button>
              </div>
            </form>
          </section>
        )}
      </main>
      <TestFooter />
    </>
  );
}
